/**
 * Shared leave-on-auth-loss for QR / checkout-complete payment surfaces.
 * Callers gate chrome with `withholdPaymentChrome` (true whenever decision==='leave',
 * including durable sessionStorage seen + token already gone on first paint after remount).
 *
 * In-memory auth-seen is not used — durable `paymentSurfaceAuthSeen` covers remount and
 * same-tab loss after mark-authed (persisted during decision when signed in).
 */

import { useLayoutEffect, useRef } from 'react';
import { clearJourneyCheckpoint } from '../../app/pwa/journeyCheckpoint.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import {
  buildSignInPathAfterPaymentAuthLoss,
  resolveLeavePaymentOnAuthLossDurable,
  type LeavePaymentOnAuthLossDecision,
} from './leavePaymentSurfaceOnAuthLoss.js';
import { clearPaymentSurfaceAuthSeen } from './paymentSurfaceAuthSeen.js';

export type UseLeavePaymentSurfaceOnAuthLossInput = {
  readonly tenantCode: string;
  /** paymentId or a stable complete-route key */
  readonly surfaceKey: string;
};

export type UseLeavePaymentSurfaceOnAuthLossResult = {
  readonly decision: LeavePaymentOnAuthLossDecision;
  /** True whenever decision==='leave' — set during render (before payment chrome paint). */
  readonly withholdPaymentChrome: boolean;
};

export function useLeavePaymentSurfaceOnAuthLoss(
  input: UseLeavePaymentSurfaceOnAuthLossInput,
): UseLeavePaymentSurfaceOnAuthLossResult {
  const { tenantCode, surfaceKey } = input;
  const { session, sessionHydrated } = useCustomerSession();
  const globallySignedIn = isGloballySignedInChrome({ session });

  const leftPaymentForAuthLossRef = useRef(false);

  const decision = resolveLeavePaymentOnAuthLossDurable({
    sessionHydrated,
    globallySignedIn,
    tenantCode,
    surfaceKey,
    hadGlobalAuthInMemory: false,
    persistMark: true,
  });

  const withholdPaymentChrome = decision === 'leave';

  useLayoutEffect(() => {
    if (decision !== 'leave' || leftPaymentForAuthLossRef.current) {
      return;
    }
    leftPaymentForAuthLossRef.current = true;
    clearJourneyCheckpoint(tenantCode);
    clearPaymentSurfaceAuthSeen(tenantCode, surfaceKey);
    window.location.replace(buildSignInPathAfterPaymentAuthLoss(tenantCode));
  }, [decision, surfaceKey, tenantCode]);

  return { decision, withholdPaymentChrome };
}
